/**
 * @file TagsMenu.tsx
 * @brief Tag editing as a dropdown, never a dialog: under its own button
 *        (`TagsMenu`) or as a "Tags" submenu of a ⋯ menu (`TagsSubmenu`).
 *
 * @details
 * DES 04-10-2026: "add a tag ko bhi drop down list style main hi banao", then
 * "jahan jahan tags attach hotay hain aisa hi karo". The web picker standard
 * (SearchableMenuList in a DropdownMenu, like Share and the ⋯ menu): the
 * search field doubles as the new-tag box, so typing a tag that doesn't exist
 * offers "Add “…”" first and Enter adds it; the rows are this item's tags
 * (ticked) then the user's other tags as suggestions; a tap adds or removes
 * one and saves at once. Same rules as the old editor (noteTags.ts):
 * lowercased, 40 characters, 12 tags at most.
 *
 * `TagsMenu`'s trigger (`anchor`) must be a native element or forward its
 * ref, and must not open the menu itself in onClick.
 *
 * @author DES
 * @date 04-10-2026
 */

import { useMemo, useState, type ReactElement } from "react";
import { Hash, Plus, Tag } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { MAX_NOTE_TAGS, mergeTags, normalizeTag } from "./noteTags";

const ADD = "__add__";

type TagsListProps = {
  tags: string[];
  suggestions: { tag: string; uses: number }[];
  saving?: boolean;
  onChange: (next: string[]) => void;
  /** Empty inside a submenu: its trigger already says "Tags". */
  title?: string;
};

/** The menu body both shapes share. */
function TagsList({ tags, suggestions, saving, onChange, title = "Tags" }: TagsListProps) {
  const [query, setQuery] = useState("");
  const draft = normalizeTag(query);
  const atLimit = tags.length >= MAX_NOTE_TAGS;

  const items: SearchableMenuItem[] = useMemo(() => {
    const seen = new Set<string>();
    const all: string[] = [];
    for (const t of [...tags, ...suggestions.map((s) => s.tag)]) {
      const n = normalizeTag(t);
      if (n && !seen.has(n)) {
        seen.add(n);
        all.push(n);
      }
    }
    const shown = draft ? all.filter((t) => t.includes(draft)) : all;
    const rows: SearchableMenuItem[] = shown.map((t) => ({
      value: t,
      label: t,
      icon: <Hash className="h-3.5 w-3.5 text-app-text-secondary" />,
      // At the limit only removing is possible.
      disabled: atLimit && !tags.includes(t),
    }));
    if (draft && !seen.has(draft)) {
      rows.unshift({
        value: ADD,
        label: `Add “${draft}”`,
        icon: <Plus className="h-3.5 w-3.5 text-brand" />,
        disabled: atLimit || draft.length > 40,
      });
    }
    return rows;
  }, [tags, suggestions, draft, atLimit]);

  return (
    <SearchableMenuList
      title={title || undefined}
      items={items}
      selected={tags}
      multiple
      query={query}
      onQueryChange={setQuery}
      searchPlaceholder="Find or add a tag"
      emptyText="Type a tag and press Enter."
      noMatchText="Type a tag and press Enter."
      loading={saving && items.length === 0}
      onSelect={(value) => {
        if (value === ADD) {
          onChange(mergeTags(tags, [draft]));
          setQuery("");
          return;
        }
        onChange(
          tags.includes(value) ? tags.filter((t) => t !== value) : mergeTags(tags, [value]),
        );
      }}
      footer={
        atLimit ? (
          <p className="px-2 pb-1 pt-2 text-xs text-muted-foreground">
            Up to {MAX_NOTE_TAGS} tags. Remove one to add another.
          </p>
        ) : null
      }
    />
  );
}

// The menus are portalled, but React still bubbles their clicks to the
// trigger's ancestors: inside a card that is a link (Home), a tap would open
// the item too.
const stop = (e: React.MouseEvent) => e.stopPropagation();

/** Tags in a dropdown under `anchor`. The caller keeps the draft. */
export function TagsMenu({
  anchor,
  open,
  onOpenChange,
  align = "start",
  side,
  sideOffset,
  ...list
}: TagsListProps & {
  anchor: ReactElement;
  /** Controlled open, so a dock can keep one menu open at a time. */
  open?: boolean;
  /** Lets the caller take a fresh draft when the menu opens. */
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  /** A dock opens it upward with InvoiceMenu's gap (DES 04-10-2026). */
  side?: "top" | "bottom";
  sideOffset?: number;
}) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>{anchor}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className="w-72 p-1.5"
        onClick={stop}
      >
        <TagsList {...list} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Tags as a "Tags" submenu of a ⋯ menu, like Share. It keeps its own draft
 * (from `tags` each time it opens) and reverts it if `save` fails.
 */
export function TagsSubmenu({
  tags,
  suggestions,
  save,
}: {
  tags: string[];
  suggestions: { tag: string; uses: number }[];
  save: (next: string[]) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<string[]>(tags);
  const [saving, setSaving] = useState(false);
  return (
    <DropdownMenuSub
      onOpenChange={(o) => {
        if (o) setDraft(tags.filter(Boolean));
      }}
    >
      <DropdownMenuSubTrigger>
        <Tag className="mr-2 h-4 w-4" />
        Tags
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-72 p-1.5" onClick={stop}>
        <TagsList
          title=""
          tags={draft}
          suggestions={suggestions}
          saving={saving}
          onChange={(next) => {
            const prev = draft;
            setDraft(next);
            setSaving(true);
            save(next)
              .catch((err: unknown) => {
                setDraft(prev);
                toast({
                  variant: "destructive",
                  title: "Could not save tags",
                  description: err instanceof ApiClientError ? err.message : "Please try again.",
                });
              })
              .finally(() => setSaving(false));
          }}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
